import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { FormGroup, Validators, FormBuilder } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Router, ActivatedRoute } from '@angular/router';
import { DsPasswordService } from '@sixense/ds-roazhon-service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'ds-lib-reset-password',
  templateUrl: './ds-lib-reset-password.component.html',
  styleUrls: ['./ds-lib-reset-password.component.css'],
})
export class DsLibResetPasswordComponent implements OnInit {
  /**
   * The subject destroyer
   */
  destroy$: Subject<boolean> = new Subject<boolean>();

  /**
   * Emit back to login mode.
   */
  @Output() backlogin = new EventEmitter();

  /**
   * The user token
   */
  @Input() token: string;

  /**
   * The button spinner state
   */
  public showSpinner: boolean;

  /**
   * The reset login form
   */
  public resetPasswordForm: FormGroup;

  /**
   * Creates an instance of DsLibResetPasswordComponent.
   *
   * @param fb creating instances of a FormControl, FormGroup, or FormArray
   * @param translate TranslateService
   * @param router ngModule that provides navigation and URL manipulation
   * @param activatedRoute contains the information about a route associated
   * @param dsPasswordService DsPasswordService
   * @param snackBar service to dispatch Material Design snack bar messages
   */
  constructor(
    private fb: FormBuilder,
    private translate: TranslateService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private dsPasswordService: DsPasswordService,
    public snackBar: MatSnackBar
  ) {}

  /**
   * Create the resetPassword form.
   */
  ngOnInit() {
    this.resetPasswordForm = this.fb.group(
      {
        newPassword: [null, Validators.required],
        confirmPassword: [null, Validators.required],
      },
      { validator: this.passwordsMatch }
    );
  }

  /**
   * Get the new password control.
   *
   * @returns AbstractControl
   */
  get newPasswordControl() {
    return this.resetPasswordForm.get('newPassword');
  }

  /**
   * Get the confirmation password control.
   *
   * @returns AbstractControl
   */
  get confirmPasswordControl() {
    return this.resetPasswordForm.get('confirmPassword');
  }

  /**
   * Check the new password & the confirmation matching.
   *
   * @param resetPasswordForm FormGroup
   * @returns hasError `noMatch` true/falsexx
   */
  passwordsMatch(resetPasswordForm: FormGroup) {
    const formValue = resetPasswordForm.value;
    if (formValue.newPassword === formValue.confirmPassword) {
      return null;
    }
    return { noMatch: true };
  }

  /**
   * Navigate to login mode.
   */
  onbackLogin() {
    this.router.navigate([], { relativeTo: this.activatedRoute });
    this.backlogin.emit();
  }

  /**
   * Switch the button spinner.
   */
  switchSpinner() {
    this.showSpinner = !this.showSpinner;
  }

  /**
   * Reset the user password.
   */
  submitForm() {
    this.dsPasswordService
      .changeUserPassword(this.token, this.newPasswordControl.value)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        /**
         *
         */
        next: () => {
          this.snackBar.open(this.translate.instant('dsLibCommon.login.passwordChanged'), null, {
            panelClass: ['mat-primary'],
            duration: 5000,
          });
          this.onbackLogin();
        },
        /**
         *
         */
        error: () => {
          this.switchSpinner();
        },
        /**
         *
         */
        complete: () => {
          this.switchSpinner();
        },
      });
  }
}
